import { propertyTypesVacantNeeded } from 'config'
import { ILoan } from 'pages/LoanStructure/interfaces'
import { getPrice3decimal, isEmpty, isNull, removeComma } from 'utils'

import {
  CommercialProductTypes,
  NonDSCRCustomLLPAKeys,
  NonDSCRCustomLLPAOptions,
  NonDSCRCustomLLPASelectKeys,
  NonDSCRCustomLLPASelectOptions,
  NonDSCRCustomRangeAdjustmentKeys,
  NonDSCRCustomRangeKeys,
  RatePriceAdjustmentKeys,
  RatePriceAdjustmentKeysOptions,
} from './constants'
import {
  type IBankruptcyRange,
  type IMortgageLatesRange,
  type IProgramData,
  type IRange,
  IMinMaxLimit,
  IVisibleOptions,
  NonDSCRTemplateType,
} from './interface'

export const NoUnitPropertyTypes = [
  'SFR-Detached',
  'SFR-Attached',
  'Condo-Warrantable',
  'Condo-NonWarrantable',
  'Condotel',
  'Co-Op',
  'PUD',
  'Modular',
  'Industrial',
  'Manufactured',
  'Self Storage',
  'Office',
  'Retail',
  'Warehouse',
  'Automotive',
]

export const convertNullValue = (value: any): string => {
  return isEmpty(value) ? 'N/A' : String(value)
}

export const convertNullValueWithDecimal = (value: any): string => {
  return isEmpty(value) ? 'N/A' : getPrice3decimal(value)
}

export const convertNullValueToTBD = (value: any): string => {
  return isEmpty(value) ? 'TBD' : getPrice3decimal(value)
}

export const convertNullValueWithPercentage = (value: any): string => {
  return isEmpty(value) ? 'N/A' : `${getPrice3decimal(value)}%`
}

export const convertNullToBlank = (value: any): string => {
  return isEmpty(value) || isNaN(value) ? '' : String(value)
}

export const convertMaxVacant = (item: {
  value: string
  maxVacant: number
  maxVacantIsPercentage: boolean
}): string => {
  const value = convertNullToBlank(item.maxVacant)
  if (value === '' || !propertyTypesVacantNeeded.includes(item.value)) return ''
  return item.maxVacantIsPercentage ? `${value}%` : value
}

export const convertLTV = (value: any): string => {
  return isEmpty(value) || isNaN(value) ? '' : Number(value) < 0 ? '0' : String(value)
}

export const convertNullToBlankForPPP = (value: any) => {
  return isEmpty(value) || isNaN(value) ? '' : `${value} ${[0, 1].includes(removeComma(value)) ? 'Month' : 'Months'}`
}

export const convertNullToNoMax = (value: any) => {
  return isEmpty(value) || isNaN(value) ? 'No Max' : getPrice3decimal(value)
}

export const convertNullToNoScore = (value: any) => {
  return isEmpty(value) || isNaN(value) ? 'No Score' : value
}

export const convertNullToPlus = (value: any) => {
  return isEmpty(value) || isNaN(value) ? '+' : getPrice3decimal(value)
}

export const convertNullToBlankValueToDecimal = (value: any) => {
  return isEmpty(value) || isNaN(value) ? '' : getPrice3decimal(value)
}

export const convertRange = (data: IMinMaxLimit) => {
  if (isNull(data.min) && isNull(data.max)) return ''
  else if (isNull(data.min) && !isNull(data.max)) return `<= ${getPrice3decimal(data.max)}`
  else if (!isNull(data.min) && isNull(data.max)) return `${getPrice3decimal(data.min)} +`
  else {
    if (data.min == data.max) return getPrice3decimal(data.min)
    return `${getPrice3decimal(data.min)} - ${getPrice3decimal(data.max)}`
  }
}

export const getPropertyTypeLabel = (value: string, numberOfUnits: IRange) => {
  let unitsRange = ''
  const { from, to } = numberOfUnits
  if (!isEmpty(from) && !isEmpty(to)) {
    if (from === to) unitsRange = ` (${from} units)`
    else unitsRange = ` (${from}-${to} units)`
  } else if (isEmpty(from) && !isEmpty(to)) unitsRange = ` (< ${to} units)`
  else if (!isEmpty(from) && isEmpty(to)) unitsRange = ` (${from}+ units)`
  return value + unitsRange
}

export const convertFICORange = (value: IRange) => {
  const { from, to } = value

  if (isNull(from) && isNull(to)) return `No Score`
  else if (!isNull(from) && isNull(to)) return `${getPrice3decimal(from)} +`
  else if (isNull(from) && !isNull(to)) return `<= ${getPrice3decimal(to)}`
  else {
    if (from === to) return `${from}`
    return `${getPrice3decimal(from)} - ${getPrice3decimal(to)}`
  }
}

export const convertRangeToString = (value: IRange, key: string = '') => {
  const { from, to } = value
  let suffix = ''
  switch (key) {
    case 'Experience':
      suffix = (from === 0 && to === 0) || (from === 1 && to === 1) ? ' Transaction' : ' Transactions'
      break
    case 'Loan Term':
    case 'Months Reserve':
    case 'Forbearance / Loan Modification':
    case 'Forbearance':
    case 'FC/SS/DIL':
    case 'Foreclosure':
    case 'Short Sale':
    case 'Deed In Lieu':
      suffix = ' Months'
      break
  }
  const isFICO = ['FICO'].includes(key)

  if (isNull(from) && isNull(to)) return isFICO ? 'No Score' : `None`
  else if (!isNull(from) && isNull(to)) return `${getPrice3decimal(from)}+${suffix}`
  else if (isNull(from) && !isNull(to)) return `<= ${getPrice3decimal(to)}${suffix}`
  else {
    if (from === to) return `${from}${suffix}`
    return `${getPrice3decimal(from)} - ${getPrice3decimal(to)}${suffix}`
  }
}

export const getBankruptcyLabel = (value: IBankruptcyRange) => {
  const { chapter, from, to } = value

  if (isNull(chapter)) return 'None'
  else if (isNull(from) && isNull(to)) return 'BK Active'
  else if (!isNull(from) && isNull(to)) return `BK${chapter} Discharged ${getPrice3decimal(from)}+ Months`
  else if (isNull(from) && !isNull(to)) return `BK${chapter} Discharged <= ${getPrice3decimal(to)} Months`
  else {
    if (from === to) return `BK${chapter} Discharged ${from} Months`
    return `BK${chapter} Discharged ${getPrice3decimal(from)} - ${getPrice3decimal(to)} Months`
  }
}

export const getMortgageLatesLabel = (value: IMortgageLatesRange) => {
  const { days, months, from, to } = value

  if (isNull(days) && isNull(months)) return 'None'
  else if (isNull(from) && isNull(to)) return 'None'
  else if (!isNull(from) && isNull(to)) return `>= ${from}X${days}X${months}`
  else if (isNull(from) && !isNull(to)) return `<= ${to}X${days}X${months}`
  else {
    if (from === to) return `${from}X${days}X${months}`
    return `${from}-${to}X${days}X${months}`
  }
}

export const getNonDscrTableData = (program: IProgramData) => {
  let rlt: any[] = []
  const isLeverageBased = program.TemplateType == NonDSCRTemplateType.LeverageBased
  const { baseRates, basePrices, rateSpreads, exitFees, ratePriceAdjustments } = program.ProgramData

  const stackCols = isLeverageBased ? [5, 6, 7, 8, 9, 10, 11, 12, 13, 14] : [5, 6, 7, 8]
  const titleRows = [0, 1]
  const titleCols = [0, 1, 2, 3, 4, 5, 6]
  const titleColsSpan2 = [0, 1, 2, 3]
  const colSpan2Cols = [1, 2, 3]
  const colSpan2Rows = [0]

  const rightAlignRows: number[] = []

  const titles: string[] = ['0-0', '1-0', '8-0']
  const rightAlignCells: string[] = []
  const colSpan2: string[] = []

  let posY = 0

  // Rate & Price Adjustment Label Rows
  titleRows.push(posY)
  colSpan2Rows.push(posY)
  titleRows.push(posY + 1)

  rlt.push(['Rate & Price Adjustments', 'Purchase', 'No-Cashout', 'Cashout'])
  rlt.push([
    '',
    'Rate Adjustment',
    'Price Adjustment',
    'Rate Adjustment',
    'Price Adjustment',
    'Rate Adjustment',
    'Price Adjustment',
  ])
  posY += 2

  ratePriceAdjustments.forEach((adjustment) => {
    const isCustom = !!adjustment?.keys
    if (!!adjustment.values.length) {
      // Title Row
      titleRows.push(posY)
      rlt.push([
        isCustom
          ? adjustment.keys?.map((key) => NonDSCRCustomLLPAOptions[key as NonDSCRCustomLLPAKeys]).join(' + ')
          : RatePriceAdjustmentKeysOptions[adjustment.key as RatePriceAdjustmentKeys],
        '',
        '',
        '',
        '',
        '',
        '',
      ])
      posY++

      // Adjustment Rows
      adjustment.values.forEach(({ value, rate, price }) => {
        rlt.push([
          isCustom
            ? adjustment.keys
                ?.map((key) => {
                  const isSelection = NonDSCRCustomLLPASelectKeys.includes(key as NonDSCRCustomLLPAKeys)
                  const isRange = NonDSCRCustomRangeKeys.includes(key as NonDSCRCustomLLPAKeys)
                  const isCustomRange = NonDSCRCustomRangeAdjustmentKeys.includes(key as NonDSCRCustomLLPAKeys)
                  if (isSelection)
                    return (
                      (NonDSCRCustomLLPASelectOptions[key as NonDSCRCustomLLPAKeys] as any)[value[key]] || value[key]
                    )
                  else if (isRange || isCustomRange)
                    return convertRangeToString(value[key], NonDSCRCustomLLPAOptions[key as NonDSCRCustomLLPAKeys])
                  else if (key === NonDSCRCustomLLPAKeys.BANKRUPTCY) return getBankruptcyLabel(value[key])
                  else if (key === NonDSCRCustomLLPAKeys.MORTGAGE_LATES) return getMortgageLatesLabel(value[key])
                  else return ''
                })
                .join(' / ')
            : adjustment.key === RatePriceAdjustmentKeys.FICO && value == 'No Score'
            ? 'No Score'
            : adjustment.key === RatePriceAdjustmentKeys.MORTGAGE_LATES
            ? getMortgageLatesLabel(value)
            : adjustment.key === RatePriceAdjustmentKeys.BANKRUPTCY
            ? getBankruptcyLabel(value)
            : adjustment.key === RatePriceAdjustmentKeys.PROPERTY_TYPE
            ? getPropertyTypeLabel(value.value, value.numberOfUnits)
            : value?.from !== undefined
            ? convertRangeToString(value, RatePriceAdjustmentKeysOptions[adjustment.key as RatePriceAdjustmentKeys])
            : value?.label
            ? value.label
            : value,
          convertNullValue(rate.purchase),
          convertNullValue(price.purchase),
          convertNullValue(rate.nocashout),
          convertNullValue(price.nocashout),
          convertNullValue(rate.cashout),
          convertNullValue(price.cashout),
        ])

        rightAlignRows.push(posY)
        posY++
      })
      posY++
      rlt.push(['', '', '', '', '', '', ''])
    }
  })
  rlt.pop()

  const totalRows =
    ratePriceAdjustments.reduce((acc, item) => acc + (item?.values?.length ? item.values.length + 2 : 0), 0) + 1

  if (isLeverageBased) {
    const { LTV, BridgeData } = program
    const isBridge = program.ProductType === CommercialProductTypes.BRIDGE
    const ltvLabel = isBridge ? 'AIV-LTV' : 'LTC'
    const ratePriceAdjustment = BridgeData?.ratePriceAdjustment ?? []
    const ensureRow = (rowIndex: number) => {
      if (!rlt[rowIndex]) rlt[rowIndex] = ['', '', '', '', '', '', '']
    }

    rlt[0] = [...rlt[0], '', `Base Rate(FICO/${ltvLabel})`]
    LTV.forEach((item) => rlt[0].push(`${item.from} - ${item.to}`))
    let index = 1
    BridgeData?.rate.forEach((item) => {
      rlt[index] = [...rlt[index], '', convertFICORange(item.FICO)]
      item.interestRates.forEach((val) => {
        rlt[index].push(convertNullValue(val))
      })
      index++
    })

    const appendRatePriceMatrix = (title: string, valuesKey: 'LLPA' | 'LLRA') => {
      ensureRow(index)
      rightAlignCells.push(`${index}-8`)
      rlt[index] = [...rlt[index], '', title]
      titles.push(`${index}-8`)
      LTV.forEach((item, ltvIndex) => {
        rlt[index].push(`${item.from} - ${item.to}`)
        titles.push(`${index}-${9 + ltvIndex}`)
      })

      index += 1
      ratePriceAdjustment.forEach((item) => {
        ensureRow(index)
        rightAlignCells.push(`${index}-8`)
        rlt[index] = [...rlt[index], '', convertFICORange(item.FICO)]
        ;(item[valuesKey] ?? []).forEach((val) => {
          rlt[index].push(convertNullValue(val))
        })
        index += 1
      })
    }

    if (ratePriceAdjustment.length) {
      appendRatePriceMatrix(`LLPA(FICO/${ltvLabel})`, 'LLPA')
      appendRatePriceMatrix(`LLRA(FICO/${ltvLabel})`, 'LLRA')
    }
    ensureRow(index)

    rlt[index] = [...rlt[index], '', '', '', '', '', '', '', '', '', '', '']
    const titleMapping: Record<string, string> = {
      rate: 'Rate',
      price: 'Price',
      exitFee: 'Exit Fee',
      rateSpread: 'Rate Spread',
    }
    ;['price', 'exitFee', 'rateSpread'].forEach((key) => {
      index++
      titles.push(`${index}-8`)
      rlt[index] = [
        ...rlt[index],
        '',
        titleMapping[key],
        convertNullValue((BridgeData as any)?.[key]),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]
    })
    ;['rate', 'price', 'exitFee', 'rateSpread'].forEach((key) => {
      index++
      titles.push(`${index}-8`)
      rlt[index] = [
        ...rlt[index],
        '',
        `Incremental ${titleMapping[key]} Change`,
        convertNullValue((BridgeData?.incremental as any)?.[key]),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]
    })
    ;['rate', 'price', 'exitFee', 'rateSpread'].forEach((key) => {
      index++
      titles.push(`${index}-8`)
      rlt[index] = [
        ...rlt[index],
        '',
        `Max ${titleMapping[key]}`,
        convertNullValue((BridgeData?.maximum as any)?.[key]),
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ]
    })

    index++
    const finalRowCount = Math.max(totalRows, index)
    for (let i = index; i < finalRowCount; i++) {
      ensureRow(i)
      rlt[i] = [...rlt[i], '', '', '', '', '', '', '', '', '', '', '']
    }
  } else {
    // Draw Rate & Price
    rlt[0] = [...rlt[0], '', 'Base Rate', 'Base Price', 'Rate Spread', 'Exit Fee']

    baseRates.forEach((_, index: number) => {
      rlt[index + 1] = [
        ...rlt[index + 1],
        '',
        convertNullValue(baseRates[index]),
        convertNullValue(basePrices[index]),
        convertNullValue(rateSpreads[index]),
        convertNullValue(exitFees[index]),
      ]
    })

    for (let i = baseRates.length + 1; i < totalRows; i++) {
      rlt[i] = [...rlt[i], '', '', '', '', '']
    }
  }

  colSpan2Rows.forEach((y) => {
    colSpan2Cols.forEach((x) => {
      colSpan2.push(`${y}-${x}`)
    })
  })

  titleRows.forEach((y) => {
    if (colSpan2Rows.includes(y)) {
      titleColsSpan2.forEach((x) => {
        titles.push(`${y}-${x}`)
      })
    } else
      titleCols.forEach((x) => {
        titles.push(`${y}-${x}`)
      })
  })

  stackCols.forEach((x) => {
    titles.push(`0-${x}`)
  })

  rightAlignRows.forEach((y) => {
    rightAlignCells.push(`${y}-0`)
  })

  return {
    table: rlt,
    style: {
      titles,
      rightAlignCells,
      colSpan2,
    },
  }
}

export const getVisibleOptions = (
  loan: ILoan,
  auth: any,
  isBorrower: boolean,
  visibleOptions: IVisibleOptions | undefined,
) => {
  const result = {
    visiblePrice: true,
    visibleExitFee: true,
    visibleRateSpread: true,
  }
  if (!visibleOptions) return result

  const userType = auth.profile.accountType
  const { price, exitFee, rateSpread } = visibleOptions

  if (!price.visible) result.visiblePrice = false
  if (!exitFee.visible) result.visibleExitFee = false
  if (!rateSpread.visible) result.visibleRateSpread = false

  if (!auth.isAuthenticated) return result

  if (!isBorrower) {
    if (!price.userTypes.includes(userType)) result.visiblePrice = false
    if (!exitFee.userTypes.includes(userType)) result.visibleExitFee = false
    if (!rateSpread.userTypes.includes(userType)) result.visibleRateSpread = false
  }

  if (isEmpty(loan.type)) return result

  if (!price.channelTypes.includes(loan.type)) result.visiblePrice = false
  if (!exitFee.channelTypes.includes(loan.type)) result.visibleExitFee = false
  if (!rateSpread.channelTypes.includes(loan.type)) result.visibleRateSpread = false

  return result
}
